import {
  otherDirectCostsItemTemplate,
  otherDirectCostsItemController
} from '../modals';

import moment from 'moment';
import { sortBy } from 'lodash';

class OtherDirectCostsController {

  constructor(CompanyService, toastr, $scope, $http, $rootScope, $stateParams, $state, $uibModal, TravelExpenseService) {
    'ngInject';

    this.moment = moment;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.apiHost = $rootScope.apiHost;
    this.toastr = toastr;
    this.state = $state;
    this.$stateParams = $stateParams;
    this.$uibModal = $uibModal;
    this.travelExpenseService = TravelExpenseService;
    this.step = 1;
    this.projects = [];

    this.report = {
      expenses: [],
      status: 'DRAFT',
      purpose: 'customer',
      reportType: 'OTHER_DIRECT_COSTS',
      project: ''
    };
    this.loading = true;
    this.proxyImport = this.$stateParams.proxyImport === true
      || this.$stateParams.proxyImport === 'true';

    this.loadProjects().then(() => this.loadExistingReportIfNeeded());
  }

  loadProjectsForUser(userId) {
    return this.travelExpenseService.getExpenseProjectOptionsForUser(userId).then((projects) => {
      this.projects = projects;
    }, () => {
      this.projects = [];
    });
  }

  loadProjects() {
    return this.$http.get(this.$rootScope.apiHost + '/api/users/me', { withCredentials: true }).then((response) => {
      const projects = (response.data.availableChargeCodes || [])
        .filter((cc) => cc.billable && cc.taskId && cc.taskName)
        .map((cc) => ({
          taskId: cc.taskId,
          taskName: cc.taskName
        }));

      this.projects = sortBy(
        Array.from(new Set(projects.map((p) => p.taskId)))
          .map((taskId) => projects.find((p) => p.taskId === taskId)),
        'taskName'
      );
    }, () => {
      this.projects = [];
    });
  }

  loadExistingReportIfNeeded() {
    const expenseReportId = this.$stateParams.expenseReportId;

    if (!expenseReportId) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.travelExpenseService.getExpenseReport(expenseReportId).then((response) => {
      const report = this.travelExpenseService.normalizeExpenseReportDates(response.data);
      const isProxy = this.proxyImport || !!report.proxyCreatedByUserId;

      if (!isProxy && report.status && report.status !== 'DRAFT' && report.status !== 'REJECTED') {
        this.loading = false;
        this.toastr.warning('This expense report cannot be edited in the create wizard.', 'Unable to Open');
        this.state.go('other-direct-costs-details', { expenseReportId: report.id });
        return;
      }

      this.proxyImport = isProxy;
      this.travelExpenseService.hydrateEstimateBasisDisplay(report);
      this.report = report;
      this.report.expenses = report.expenses || [];
      this.report.reportType = 'OTHER_DIRECT_COSTS';
      this.report.purpose = 'customer';
      if (!this.report.project) {
        this.report.project = '';
      }

      const projectLoad = isProxy && report.userId
        ? this.loadProjectsForUser(report.userId)
        : Promise.resolve();

      return projectLoad.then(() => {
        this.step = this.resolveWizardStep(report);
        this.loading = false;
      });
    }, () => {
      this.loading = false;
      this.toastr.error('Could not load the expense report.', 'Error');
      this.state.go(this.proxyImport ? 'admin.expenses' : 'expenses');
    });
  }

  proxyBannerText() {
    const report = this.report || {};
    const employee = [report.firstName, report.lastName].filter(Boolean).join(' ') || 'employee';
    const manager = [report.proxyCreatedByFirstName, report.proxyCreatedByLastName].filter(Boolean).join(' ');
    if (manager) {
      return 'Entered by ' + manager + ' on behalf of ' + employee + '. Status emails are not sent.';
    }
    return 'Proxy import on behalf of ' + employee + '. Status emails are not sent.';
  }

  openReviewDetails() {
    if (!this.report || !this.report.id) {
      return;
    }
    this.state.go('other-direct-costs-review-details', { expenseReportId: this.report.id });
  }

  openActuals() {
    if (!this.report || !this.report.id) {
      return;
    }
    const params = { expenseReportId: this.report.id };
    if (this.proxyImport) {
      params.proxyImport = 'true';
    }
    this.state.go('other-direct-costs-actuals', params);
  }

  resolveWizardStep(report) {
    const savedStep = Number(report && report.wizardStep);
    if (savedStep >= 1 && savedStep <= 3) {
      return savedStep;
    }

    return this.getItems().length ? 2 : 1;
  }

  hasBillableProjects() {
    return this.projects && this.projects.length > 0;
  }

  getProjectName() {
    if (!this.report.project) {
      return this.report.projectName || '';
    }

    const match = (this.projects || []).find((project) => String(project.taskId) === String(this.report.project));
    return match ? match.taskName : (this.report.projectName || String(this.report.project));
  }

  getItems() {
    return (this.report.expenses || []).filter((expense) => !expense.itinerary);
  }

  formatDate(value) {
    return value ? moment(value).format('MMM DD, YYYY') : '';
  }

  addItem() {
    this.openItemModal(null);
  }

  editItem(expense) {
    this.openItemModal(expense);
  }

  openItemModal(expenseToEdit) {
    const self = this;
    const modalInstance = this.$uibModal.open({
      template: otherDirectCostsItemTemplate,
      controller: otherDirectCostsItemController,
      controllerAs: '$ctrl',
      resolve: {
        existingExpense() {
          return expenseToEdit || null;
        }
      },
      windowClass: 'hmodal-success'
    });

    modalInstance.addItemToReport = function(expense) {
      self.report.expenses.push(expense);
    };

    modalInstance.updateItemInReport = function(original, updated) {
      self.applyItemEdits(original, updated);
    };
  }

  applyItemEdits(original, updated) {
    const expenses = this.report.expenses || [];
    const index = expenses.indexOf(original);

    if (index === -1) {
      return;
    }

    const target = expenses[index];
    target.type = updated.type;
    target.description = updated.description;
    target.amount = updated.amount;
    target.dateNeeded = updated.dateNeeded;
    target.documentationLink = updated.documentationLink;
    target.estimateBasisFile = updated.estimateBasisFile;
    target.estimateBasisFileName = updated.estimateBasisFileName;

    if (updated.estimateBasisFile) {
      target.pendingReceiptIdsToDelete = (target.receipts || [])
        .map((receipt) => receipt.receipt || receipt.id)
        .filter(Boolean);
      target.receipts = [];
    }
  }

  removeItem(index) {
    const items = this.getItems();
    const target = items[index];
    const expenses = this.report.expenses || [];
    const actualIndex = expenses.indexOf(target);
    if (actualIndex !== -1) {
      expenses.splice(actualIndex, 1);
    }
  }

  getItemsTotal() {
    return this.getItems().reduce((total, expense) => total + (expense.amount || 0), 0);
  }

  hasItems() {
    return this.getItems().length > 0;
  }

  cancel() {
    this.state.go('expenses');
  }

  goToStep(step) {
    if (step < this.step) {
      this.step = step;
      return;
    }

    if (step >= 2 && !this.canProceedFromBasicInfo()) {
      return;
    }

    if (step === 3 && !this.hasItems()) {
      return;
    }

    this.step = step;
  }

  continueToItems() {
    if (!this.canProceedFromBasicInfo()) {
      this.toastr.warning('Complete all required basic information fields, including project.', 'Incomplete');
      return;
    }

    this.step = 2;
  }

  continueToSummary() {
    if (!this.hasItems()) {
      this.toastr.warning('Add at least one expense item before continuing.', 'Incomplete');
      return;
    }

    this.step = 3;
  }

  canProceedFromBasicInfo() {
    const report = this.report;

    if (!report.name || !report.name.trim()) {
      return false;
    }

    if (!report.description || !report.description.trim()) {
      return false;
    }

    if (!this.hasBillableProjects() || !report.project) {
      return false;
    }

    return true;
  }

  canSaveDraft() {
    return !!(this.report.name && this.report.name.trim());
  }

  canSubmitExpenseReport() {
    return this.canProceedFromBasicInfo()
      && this.hasItems()
      && this.getItemsTotal() > 0;
  }

  prepareReportForSave(status) {
    this.report.status = status;
    this.report.purpose = 'customer';
    this.report.reportType = 'OTHER_DIRECT_COSTS';
    this.report.wizardStep = this.step;
  }

  saveDraft() {
    if (!this.canSaveDraft()) {
      this.toastr.warning('Enter a Report Name before saving a draft.', 'Incomplete');
      return;
    }

    this.loading = true;
    this.prepareReportForSave('DRAFT');

    this.travelExpenseService.saveDraftExpenseReport(this.report).then((response) => {
      const savedReport = response.data;

      return this.travelExpenseService.uploadPendingEstimateBasisFiles(this.report, savedReport).then((refreshedReport) => {
        this.applySavedReport(refreshedReport || savedReport);
        this.loading = false;
        this.toastr.success('Draft expense report saved. You can reopen it anytime to continue.', 'Draft Saved');
      }, () => {
        this.applySavedReport(savedReport);
        this.loading = false;
        this.toastr.warning('Draft saved, but one or more documents could not be uploaded.', 'Upload Warning');
      });
    }, (error) => {
      this.loading = false;
      this.toastr.error(this.getApiErrorMessage(error, 'Could not save the draft expense report.'), 'Error');
    });
  }

  applySavedReport(savedReport) {
    const normalized = this.travelExpenseService.normalizeExpenseReportDates(savedReport);
    this.travelExpenseService.hydrateEstimateBasisDisplay(normalized);
    const previousStep = this.step;

    this.report = normalized;
    this.report.expenses = normalized.expenses || [];
    this.report.reportType = 'OTHER_DIRECT_COSTS';
    this.report.purpose = 'customer';
    this.step = previousStep || this.resolveWizardStep(normalized);

    if (this.report.id && String(this.$stateParams.expenseReportId) !== String(this.report.id)) {
      this.state.go('other-direct-costs', { expenseReportId: this.report.id }, { location: 'replace', notify: false });
    }
  }

  createExpenseReport() {
    if (!this.canSubmitExpenseReport()) {
      this.toastr.warning('Complete basic information, select a project, and add at least one expense item before submitting.', 'Incomplete');
      return;
    }

    this.loading = true;
    this.prepareReportForSave('SUBMITTED');

    this.travelExpenseService.submitExpenseReportForApproval(this.report).then((response) => {
      const createdReport = response.data;

      return this.travelExpenseService.uploadPendingEstimateBasisFiles(this.report, createdReport).then(() => {
        this.loading = false;
        this.toastr.success('Other Direct Costs report submitted for approval.', 'Submitted');
        this.state.go('expenses');
      }, () => {
        this.loading = false;
        this.toastr.warning('The request was submitted, but one or more documents could not be uploaded.', 'Upload Warning');
        this.state.go('expenses');
      });
    }, (error) => {
      this.loading = false;
      this.toastr.error(this.getApiErrorMessage(error, 'Could not submit the expense report.'), 'Error');
    });
  }

  getApiErrorMessage(error, fallback) {
    const data = error && error.data;
    if (!data) {
      return fallback;
    }

    if (typeof data === 'string' && data.trim()) {
      return data;
    }

    if (data.message) {
      return data.message;
    }

    if (data.error && typeof data.error === 'string') {
      return data.error;
    }

    return fallback;
  }
}

export default OtherDirectCostsController;



// WEBPACK FOOTER //
// ./client/pages/expenses/other-direct-costs/other-direct-costs.controller.js